import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { DEFAULT_THEME } from "../mock/mock";
import { authAPI, settingsAPI, usersAPI } from "../lib/api";

const AppContext = createContext(null);

export const AppProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    try {
      const s = localStorage.getItem("dmr_theme");
      return s ? JSON.parse(s) : DEFAULT_THEME;
    } catch (err) { console.warn("theme parse failed", err); return DEFAULT_THEME; }
  });
  const [user, setUser] = useState(() => {
    try {
      const s = localStorage.getItem("dmr_user");
      return s ? JSON.parse(s) : null;
    } catch (err) { console.warn("user parse failed", err); return null; }
  });
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Apply theme CSS variables
  useEffect(() => {
    localStorage.setItem("dmr_theme", JSON.stringify(theme));
    document.documentElement.style.setProperty("--brand-primary", theme.primary);
    document.documentElement.style.setProperty("--brand-primary-dark", theme.primaryDark);
    document.documentElement.style.setProperty("--brand-accent", theme.accent);
    // Favicon
    const link = document.querySelector("link[rel~='icon']") || document.createElement("link");
    link.rel = "icon";
    link.href = theme.favicon;
    document.head.appendChild(link);
    document.title = theme.appName || "WARGA RT DM RESIDENCE";
    // Update Open Graph meta tags dynamically so shared links show latest branding
    const setMeta = (attr, key, val) => {
      if (!val) return;
      let el = document.querySelector(`meta[${attr}="${key}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute(attr, key); document.head.appendChild(el); }
      el.setAttribute("content", val);
    };
    const origin = window.location.origin;
    // OG image WAJIB absolute & ringan (<300KB) agar WhatsApp/Telegram menampilkan preview.
    // Hero upload admin bisa berupa PNG 2MB+ atau path relatif /api/files/... → keduanya ditolak crawler.
    const ogImg = `${origin}/og-image.jpg`;
    const desc = `Platform Digital Sapa Warga DM Residence Garut. ${theme.appTagline || ""}. Founder & Development by. Indra Arica, SE, MM`;
    setMeta("property", "og:title", `${theme.appName} — ${theme.appTagline}`);
    setMeta("property", "og:description", desc);
    setMeta("property", "og:image", ogImg);
    setMeta("property", "og:image:secure_url", ogImg);
    setMeta("property", "og:image:type", "image/jpeg");
    setMeta("property", "og:image:width", "1200");
    setMeta("property", "og:image:height", "630");
    setMeta("property", "og:url", origin + window.location.pathname);
    setMeta("property", "og:site_name", theme.appName);
    setMeta("name", "twitter:title", `${theme.appName} — ${theme.appTagline}`);
    setMeta("name", "twitter:description", desc);
    setMeta("name", "twitter:image", ogImg);
    setMeta("name", "description", desc);
  }, [theme]);

  // Bootstrap: fetch settings + verify auth + poll settings every 30s for realtime updates
  useEffect(() => {
    let mounted = true;
    const fetchSettings = async () => {
      try {
        const s = await settingsAPI.get();
        if (mounted && s && Object.keys(s).length) setTheme((t) => ({ ...t, ...s }));
      } catch (err) { console.warn("Settings fetch failed:", err?.message || err); }
    };
    (async () => {
      await fetchSettings();
      // Skip /me check if OAuth callback in progress — AuthCallback will handle it
      if (window.location.hash?.includes("session_id=")) {
        if (mounted) setLoading(false);
        return;
      }
      // Try /me regardless of localStorage token (cookie may exist)
      try {
        const u = await authAPI.me();
        if (mounted) setUser(u);
      } catch (err) {
        localStorage.removeItem("dmr_token");
        localStorage.removeItem("dmr_user");
        if (mounted) setUser(null);
      }
      if (mounted) setLoading(false);
    })();
    // Poll settings every 30s so hero image / logo / bank info update tanpa refresh
    const iv = setInterval(fetchSettings, 30000);
    const onFocus = () => fetchSettings();
    window.addEventListener("focus", onFocus);
    return () => { mounted = false; clearInterval(iv); window.removeEventListener("focus", onFocus); };
  }, []);

  useEffect(() => {
    if (user) localStorage.setItem("dmr_user", JSON.stringify(user));
    else localStorage.removeItem("dmr_user");
  }, [user]);

  const login = async (email, password) => {
    try {
      const res = await authAPI.login(email, password);
      localStorage.setItem("dmr_token", res.token);
      setUser(res.user);
      return { ok: true, user: res.user };
    } catch (e) {
      return { ok: false, error: e.response?.data?.detail || "Login gagal" };
    }
  };

  const logout = async () => {
    try { await authAPI.logout(); } catch (err) { console.warn("Logout call failed:", err?.message || err); }
    localStorage.removeItem("dmr_token");
    localStorage.removeItem("dmr_user");
    setUser(null);
  };

  const updateTheme = async (patch) => {
    setTheme((t) => ({ ...t, ...patch }));
    if (user?.role === "super_admin") {
      try { await settingsAPI.update(patch); }
      catch (err) { console.warn("Settings save failed:", err?.message || err); }
    }
  };
  const resetTheme = async () => {
    setTheme(DEFAULT_THEME);
    if (user?.role === "super_admin") {
      try { await settingsAPI.update(DEFAULT_THEME); }
      catch (err) { console.warn("Settings reset failed:", err?.message || err); }
    }
  };

  const refreshUsers = async () => {
    try { const list = await usersAPI.list(); setUsers(list); return list; } catch { return []; }
  };
  const refreshCurrentUser = async () => {
    try { const u = await authAPI.me(); setUser(u); return u; } catch { return null; }
  };
  const addUser = async (u) => { const created = await usersAPI.create(u); await refreshUsers(); return created; };
  const updateUser = async (id, patch) => { await usersAPI.update(id, patch); await refreshUsers(); };
  const deleteUser = async (id) => { await usersAPI.remove(id); await refreshUsers(); };

  const contextValue = useMemo(
    () => ({ theme, updateTheme, resetTheme, user, login, logout, refreshCurrentUser, users, refreshUsers, addUser, updateUser, deleteUser, loading }),
    [theme, user, users, loading]
  );

  return (
    <AppContext.Provider value={contextValue}>
      {children}
    </AppContext.Provider>
  );
};

export const useApp = () => {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useApp must be inside AppProvider");
  return ctx;
};
